/**
 * Admin User Management — list Iron Phoenix users and revoke/reinstate CLI tokens.
 * Flood alerts appear in the Backstage Notifications page for admins.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  configApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { UserEntity } from '@backstage/catalog-model';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';

type CliBlock = {
  userEntityRef: string;
  status: 'suspended' | 'revoked';
  reason: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

type UserRow = {
  username: string;
  displayName: string;
  userEntityRef: string;
  email: string;
  block?: CliBlock;
};

async function waitForIdentity(identityApi: {
  getBackstageIdentity: () => Promise<unknown>;
}): Promise<void> {
  for (let i = 0; i < 6; i++) {
    try {
      await identityApi.getBackstageIdentity();
      return;
    } catch {
      await new Promise(r => setTimeout(r, 150 * (i + 1)));
    }
  }
}

function statusLabel(block?: CliBlock): string {
  if (!block) return 'active';
  return block.status;
}

export const UserManagementPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const adminMember = useAdminGroupMember();

  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<UserRow[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<UserRow | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  const backend = config.getString('backend.baseUrl').replace(/\/$/, '');

  const getSignedInUsername = useCallback(async (): Promise<string | null> => {
    try {
      const identity = await identityApi.getBackstageIdentity();
      const ref = identity?.userEntityRef || '';
      const m = /^user:default\/(.+)$/i.exec(ref);
      return m?.[1]?.toLowerCase() ?? null;
    } catch {
      return null;
    }
  }, [identityApi]);

  useEffect(() => {
    let cancelled = false;
    waitForIdentity(identityApi).then(() => {
      if (!cancelled) setAuthReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const withAuth = useCallback(
    async (path: string, init?: RequestInit) => {
      const { token } = await identityApi.getCredentials();
      return fetchApi.fetch(`${backend}/api/cli-auth${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(init?.headers || {}),
        },
      });
    },
    [backend, fetchApi, identityApi],
  );

  const load = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const [entities, blocksRes] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'User' },
          fields: [
            'kind',
            'metadata.name',
            'metadata.title',
            'spec.profile.displayName',
            'spec.profile.email',
          ],
        }),
        withAuth('/admin/blocks'),
      ]);
      const blocksBody = await blocksRes.json();
      if (!blocksRes.ok) {
        const detail =
          blocksBody.error || `Failed to load blocks (${blocksRes.status})`;
        throw new Error(
          `${detail} (Blocks list unavailable — Reinstate stays hidden and everyone looks "active". If this is your own revoke, ask another admin to reinstate you, then reload.)`,
        );
      }
      const blocks = (blocksBody.items ?? []) as CliBlock[];
      const blockByRef = new Map(
        blocks.map(b => [b.userEntityRef.toLowerCase(), b]),
      );

      const users = (entities.items as UserEntity[])
        .filter(u => !/^sa_/i.test(u.metadata.name))
        .map(u => {
          const userEntityRef = `user:default/${u.metadata.name}`;
          return {
            username: u.metadata.name,
            displayName:
              u.spec?.profile?.displayName ||
              u.metadata.title ||
              u.metadata.name,
            userEntityRef,
            email: u.spec?.profile?.email || '',
            block: blockByRef.get(userEntityRef.toLowerCase()),
          };
        })
        .sort((a, b) =>
          a.displayName.localeCompare(b.displayName, undefined, {
            sensitivity: 'base',
          }),
        );

      setRows(users);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setLoading(false);
    }
  }, [catalogApi, withAuth]);

  useEffect(() => {
    if (adminMember === true && authReady) {
      void load();
    }
  }, [adminMember, authReady, load]);

  const onRevoke = async () => {
    if (!confirm) return;
    const target = confirm;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const signedIn = await getSignedInUsername();
      const reasonText = revokeReason.trim() || 'Revoked by administrator';
      const res = await withAuth(
        `/admin/users/${encodeURIComponent(target.username)}/revoke-tokens`,
        {
          method: 'POST',
          body: JSON.stringify({
            reason: revokeReason.trim() || undefined,
          }),
        },
      );
      let body: { error?: string; block?: CliBlock } = {};
      try {
        body = await res.json();
      } catch {
        body = {};
      }
      if (!res.ok) {
        throw new Error(body.error || `Revoke failed (${res.status})`);
      }

      // Revoke already succeeded — do not surface a later reload failure as "revoke failed".
      setConfirm(null);
      setRevokeReason('');
      const selfRevoke =
        !!signedIn && signedIn === target.username.toLowerCase();
      if (selfRevoke) {
        setInfo(
          `CLI access for ${target.username} was revoked (including your own session). Ask another admin to click Reinstate for you, then run iron-phoenix auth login again.`,
        );
      }

      // Optimistic row update so Reinstate appears even if reload fails.
      const now = new Date().toISOString();
      setRows(prev =>
        prev.map(row =>
          row.username.toLowerCase() === target.username.toLowerCase()
            ? {
                ...row,
                block: body.block ?? {
                  userEntityRef: target.userEntityRef,
                  status: 'revoked',
                  reason: reasonText,
                  createdBy: signedIn ? `user:default/${signedIn}` : null,
                  createdAt: now,
                  updatedAt: now,
                },
              }
            : row,
        ),
      );

      const reloaded = await load();
      if (!reloaded && !selfRevoke) {
        setInfo(
          `Revoked ${target.username}, but the user list could not fully refresh. Reload the page — Reinstate should still appear for that user.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReinstate = async (user: UserRow) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await withAuth(
        `/admin/users/${encodeURIComponent(user.username)}/reinstate`,
        { method: 'POST', body: '{}' },
      );
      let body: { error?: string } = {};
      try {
        body = await res.json();
      } catch {
        body = {};
      }
      if (!res.ok) {
        throw new Error(body.error || `Reinstate failed (${res.status})`);
      }
      setRows(prev =>
        prev.map(row =>
          row.username.toLowerCase() === user.username.toLowerCase()
            ? { ...row, block: undefined }
            : row,
        ),
      );
      const reloaded = await load();
      if (!reloaded) {
        setInfo(
          `Reinstated ${user.username}, but the user list could not fully refresh. Reload the page to confirm status is active.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (adminMember === null || !authReady) {
    return (
      <Page themeId="home">
        <Header title="User Management" subtitle="Admin tools" />
        <Content>
          <Box display="flex" justifyContent="center" minHeight={200}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (adminMember !== true) {
    return (
      <Page themeId="home">
        <Header title="User Management" subtitle="Admin tools" />
        <Content>
          <Box p={3} maxWidth={560}>
            <Typography paragraph>
              You do not have access to this page. User Management is limited to
              administrators.
            </Typography>
            <Button component={RouterLink} to="/catalog" variant="outlined">
              Back to Home
            </Button>
          </Box>
        </Content>
      </Page>
    );
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(
        r =>
          r.username.toLowerCase().includes(q) ||
          r.displayName.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          statusLabel(r.block).includes(q),
      )
    : rows;

  let tableBody: JSX.Element;
  if (loading) {
    tableBody = <Progress />;
  } else if (filtered.length === 0) {
    tableBody = (
      <Typography color="textSecondary">
        {rows.length === 0
          ? 'No catalog users found.'
          : `No users match “${search.trim()}”.`}
      </Typography>
    );
  } else {
    tableBody = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Display name</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>CLI status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map(user => (
            <TableRow key={user.userEntityRef} hover>
              <TableCell>{user.displayName}</TableCell>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.email || '—'}</TableCell>
              <TableCell>
                {statusLabel(user.block)}
                {user.block?.reason ? (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {user.block.reason}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align="right">
                {user.block ? (
                  <Button
                    size="small"
                    color="primary"
                    disabled={busy}
                    onClick={() => void onReinstate(user)}
                  >
                    Reinstate
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="secondary"
                    disabled={busy}
                    onClick={() => setConfirm(user)}
                  >
                    Revoke tokens
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Page themeId="home">
      <Header
        title="User Management"
        subtitle="Revoke or reinstate human CLI access and refresh tokens"
      />
      <Content>
        {error && (
          <Typography color="error" paragraph>
            {error}
          </Typography>
        )}
        {info && (
          <Typography color="primary" paragraph>
            {info}
          </Typography>
        )}

        <InfoCard title="Users">
          <Typography paragraph color="textSecondary">
            Revoking deletes all CLI refresh tokens and denies access JWTs for that
            user. Flood suspensions also appear under{' '}
            <RouterLink to="/notifications">Notifications</RouterLink> for admins.
          </Typography>
          <Box mb={2} maxWidth={420}>
            <TextField
              label="Search users"
              value={search}
              onChange={e => setSearch(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="name, username, email, or status"
            />
          </Box>
          {tableBody}
        </InfoCard>

        <Dialog
          open={Boolean(confirm)}
          onClose={() => !busy && setConfirm(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Revoke CLI tokens</DialogTitle>
          <DialogContent>
            <Typography paragraph>
              Revoke all access and refresh tokens for{' '}
              <strong>{confirm?.displayName}</strong> ({confirm?.username})? Their
              CLI sessions stop immediately.
            </Typography>
            <TextField
              label="Reason (optional)"
              value={revokeReason}
              onChange={e => setRevokeReason(e.target.value)}
              fullWidth
              margin="dense"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              disabled={busy}
              onClick={() => void onRevoke()}
            >
              Revoke tokens
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
